import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';

import { decode } from '@/utils/custom-enc-dec';
import { camelToSnake } from '@/utils/index';

const ENVIRONMENT = process.env.VUE_APP_ENVIRONMENT ?? 'DEV';
const URL_DIGICARE = process.env[`VUE_APP_URL_DIGICARE_${ENVIRONMENT}`];

const baseHttpClient = axios.create({
  baseURL: URL_DIGICARE,
  headers: {
    'Content-Type': 'application/json',
  },
});

const customPost = async <T = any, D = any>(
  url: string,
  data: D | undefined,
  config: AxiosRequestConfig<D> | undefined,
): Promise<AxiosResponse<T>> => {
  const snakeData = camelToSnake(data);
  const response = await baseHttpClient({
    method: 'POST',
    url,
    data: snakeData,
    ...config,
  });
  return response;
};

const httpClient = Object.assign(baseHttpClient, {
  post: customPost,
});

const customErrorHandler = (error: any) => {
  if (!error.response) {
    console.error('careClient customErrorHandler', error);
    return error;
  }

  switch (error.response.status) {
    case 401:
      // clear localStorage
      localStorage.clear();
      // redirect to login
      // eslint-disable-next-line no-case-declarations
      const { VUE_APP_DIGI_USERS_F } = process.env;
      window.location.href = `${VUE_APP_DIGI_USERS_F}/auth/login?app=care`;
      break;
    case 403:
      console.error('Forbidden');
      break;
    case 404:
      console.error('Not Found');
      break;
    case 500:
      console.error('Internal Server Error');
      break;
    default:
      console.error('Error');
      break;
  }

  return error.response.data;
};

baseHttpClient.interceptors.request.use((config) => {
  const modifiedConfig = config;

  const token = localStorage.getItem('token');
  if (token) {
    modifiedConfig.headers.Authorization = `DGTK ${token}`;
  }

  const project = localStorage.getItem('project');
  if (project) {
    const { value } = decode(project) as any;
    // get the project id. Could be 'ID' or 'id'
    const projectId = value.ID || value.id;
    modifiedConfig.headers['Dg-Project-Id'] = projectId;
  }

  return modifiedConfig;
});

httpClient.interceptors.response.use(
  (response) => response,
  (error) => {
    customErrorHandler(error);
    return Promise.reject(error);
  },
);

export default httpClient;
